/**
 * Composable for managing document filter state (multi-select)
 *
 * This composable is shared between:
 * - nmed-doc-search-filter-store
 * - projects-store
 * - saved-documents-store
 */
import type {
   FilterState,
   SearchKeyItem,
   SortDirection,
   SortOrder,
   YearFilterState,
} from '@/types/nmed/shared-filter-types';
import type { ComputedRef } from 'vue';
import {
   createEmptyFilterState,
   createEmptyYearFilter,
} from '@/types/nmed/shared-filter-types';
import { computed, reactive } from 'vue';

/* ---------------------------------------------------------------------------------------------- */
/*                                           TYPES                                                */
/* ---------------------------------------------------------------------------------------------- */

export interface UseDocumentFilterStateReturn {
   /**
    * The reactive filter state
    */
   filterState: FilterState;

   /**
    * Whether any filter is currently applied
    */
   isFilterApplied: ComputedRef<boolean>;

   /**
    * Gets the selected bureaus as an array
    */
   getSelectedBureaus: () => string[];

   /**
    * Gets the selected document types as an array
    */
   getSelectedDocTypes: () => string[];

   /**
    * Converts grouped search filters to parallel arrays for API.
    * Date fields with exactly 2 values are converted to RangeFilter objects.
    */
   getSearchFiltersAsArrays: () => { fields: string[]; values: SearchKeyItem[] };

   /**
    * Gets year parameters for API
    */
   getYearParams: () => { year: string; yearFrom: string; yearTo: string };

   /**
    * Sets the bureau(s) filter
    */
   setBureaus: (bureaus: string[]) => void;

   /**
    * Sets the document type(s) filter
    */
   setDocumentTypes: (documentTypes: string[]) => void;

   /**
    * Sets the year filter state
    */
   setYear: (year: Partial<YearFilterState>) => void;

   /**
    * Sets field-specific search filters from grouped structure
    */
   setSearchFilters: (filters: Record<string, SearchKeyItem[]>) => void;

   /**
    * Toggles a search filter value for a specific field
    */
   toggleSearchFilterValue: (field: string, value: string, mode: 'ADD' | 'REMOVE') => void;

   /**
    * Sets the global search keyword
    */
   setGlobalSearchKeyword: (keyword: string) => void;

   /**
    * Sets the sort field and order
    */
   setSortConfig: (field: string, direction: SortDirection) => void;

   /**
    * Clears all filters to their default state
    */
   clearAllFilters: () => void;

   /**
    * Clears only the search keywords (field-specific and global)
    */
   clearSearchKeywords: () => void;

   /**
    * Applies a meta attribute filter with intelligent mode selection
    */
   applyMetaAttributeFilter: (
      fieldName: string,
      value: string,
      mode?: 'ADD' | 'ONLY' | 'REMOVE',
   ) => void;
}

/* ---------------------------------------------------------------------------------------------- */
/*                                       COMPOSABLE                                               */
/* ---------------------------------------------------------------------------------------------- */

export function useDocumentFilterState(): UseDocumentFilterStateReturn {
   const filterState = reactive(createEmptyFilterState()) as FilterState;

   /**
    * Whether any filter is currently applied
    */
   const isFilterApplied = computed(() => {
      return (
         filterState.bureaus.length > 0
         || filterState.documentTypes.length > 0
         || Object.keys(filterState.searchKeyword.filters).length > 0
         || filterState.globalSearchKeyword !== ''
         || filterState.year.year !== ''
         || filterState.year.startYear !== ''
         || filterState.year.endYear !== ''
      );
   });

   /**
    * Gets selected bureaus as an array
    */
   function getSelectedBureaus(): string[] {
      return filterState.bureaus;
   }

   /**
    * Gets selected document types as an array
    */
   function getSelectedDocTypes(): string[] {
      return filterState.documentTypes;
   }

   /**
    * Converts grouped search filters to parallel arrays for API.
    * RangeFilter objects are passed through as-is; strings are treated as regular search values.
    */
   function getSearchFiltersAsArrays(): { fields: string[]; values: SearchKeyItem[] } {
      const fields: string[] = [];
      const values: SearchKeyItem[] = [];

      for (const [field, vals] of Object.entries(filterState.searchKeyword.filters)) {
         for (const value of vals) {
            fields.push(field);
            values.push(value);
         }
      }

      return { fields, values };
   }

   /**
    * Gets year parameters for API
    */
   function getYearParams(): { year: string; yearFrom: string; yearTo: string } {
      if (filterState.year.type === 'single') {
         return {
            year: filterState.year.year,
            yearFrom: '',
            yearTo: '',
         };
      }
      return {
         year: '',
         yearFrom: filterState.year.startYear,
         yearTo: filterState.year.endYear,
      };
   }

   /**
    * Sets the bureau(s) filter
    */
   function setBureaus(bureaus: string[]): void {
      filterState.bureaus = bureaus;
   }

   /**
    * Sets the document type(s) filter
    */
   function setDocumentTypes(documentTypes: string[]): void {
      filterState.documentTypes = documentTypes;
   }

   /**
    * Sets year filter state
    */
   function setYear(year: Partial<YearFilterState>): void {
      if (year.type === 'range') {
         filterState.year.year = '';
      }
      else if (year.type === 'single') {
         filterState.year.startYear = '';
         filterState.year.endYear = '';
      }
      Object.assign(filterState.year, year);
   }

   /**
    * Sets field-specific search filters from grouped structure
    */
   function setSearchFilters(filters: Record<string, SearchKeyItem[]>): void {
      filterState.searchKeyword.filters = { ...filters };
   }

   /**
    * Toggles a search filter value for a specific field
    */
   function toggleSearchFilterValue(field: string, value: string, mode: 'ADD' | 'REMOVE'): void {
      const filters = filterState.searchKeyword.filters;

      if (mode === 'ADD') {
         if (!filters[field]) {
            filters[field] = [];
         }
         if (!filters[field].includes(value)) {
            filters[field].push(value);
         }
      }
      else if (mode === 'REMOVE') {
         if (filters[field]) {
            const index = filters[field].indexOf(value);
            if (index !== -1) {
               filters[field].splice(index, 1);
               // Clean up empty arrays
               if (filters[field].length === 0) {
                  delete filters[field];
               }
            }
         }
      }
   }

   /**
    * Sets the global search keyword
    */
   function setGlobalSearchKeyword(keyword: string): void {
      filterState.globalSearchKeyword = keyword;
   }

   /**
    * Sets the sort field and order
    */
   function setSortConfig(field: string, direction: SortDirection): void {
      filterState.sortField = field;
      filterState.sortOrder = field ? (direction.toLowerCase() as SortOrder) : '';
   }

   /**
    * Clears all filters to their default state
    */
   function clearAllFilters(): void {
      filterState.bureaus = [];
      filterState.documentTypes = [];
      filterState.searchKeyword.filters = {};
      filterState.globalSearchKeyword = '';
      filterState.year = createEmptyYearFilter();
      filterState.sortField = '';
      filterState.sortOrder = '';
   }

   /**
    * Clears only the search keywords (field-specific and global)
    */
   function clearSearchKeywords(): void {
      filterState.searchKeyword.filters = {};
      filterState.globalSearchKeyword = '';
   }

   /**
    * Applies a meta attribute filter with intelligent mode selection
    */
   function applyMetaAttributeFilter(
      fieldName: string,
      value: string,
      mode?: 'ADD' | 'ONLY' | 'REMOVE',
   ): void {
      const filters = filterState.searchKeyword.filters;

      // Find existing field key (case-insensitive)
      const existingKey = Object.keys(filters).find(
         k => k.toLowerCase() === fieldName.toLowerCase(),
      );

      // Check if this attribute/value already exists
      const valueExists = existingKey ? filters[existingKey]?.includes(value) : false;

      if (mode === 'REMOVE') {
         if (existingKey && valueExists) {
            const values = filters[existingKey];
            const idx = values.indexOf(value);
            if (idx !== -1) {
               values.splice(idx, 1);
               if (values.length === 0) {
                  delete filters[existingKey];
               }
            }
         }
      }
      else if (mode === 'ONLY') {
         // Clear search keywords and set only this pair
         filterState.globalSearchKeyword = '';
         filterState.searchKeyword.filters = { [fieldName]: [value] };
      }
      else if (mode === 'ADD') {
         if (existingKey) {
            if (!filters[existingKey].includes(value)) {
               filters[existingKey].push(value);
            }
         }
         else {
            filters[fieldName] = [value];
         }
      }
      else {
         // Auto mode selection
         if (valueExists && existingKey) {
            // REMOVE: attribute/value is already in filter
            const values = filters[existingKey];
            const idx = values.indexOf(value);
            if (idx !== -1) {
               values.splice(idx, 1);
               if (values.length === 0) {
                  delete filters[existingKey];
               }
            }
         }
         else if (Object.keys(filters).length > 0) {
            // ADD: append to existing filters
            if (existingKey) {
               if (!filters[existingKey].includes(value)) {
                  filters[existingKey].push(value);
               }
            }
            else {
               filters[fieldName] = [value];
            }
         }
         else {
            // ONLY: start fresh search with just this attribute/value
            filterState.globalSearchKeyword = '';
            filterState.searchKeyword.filters = { [fieldName]: [value] };
         }
      }
   }

   return {
      filterState,
      isFilterApplied,
      getSelectedBureaus,
      getSelectedDocTypes,
      getSearchFiltersAsArrays,
      getYearParams,
      setBureaus,
      setDocumentTypes,
      setYear,
      setSearchFilters,
      toggleSearchFilterValue,
      setGlobalSearchKeyword,
      setSortConfig,
      clearAllFilters,
      clearSearchKeywords,
      applyMetaAttributeFilter,
   };
}
